import Header from "../components/Header";
import Footer from "../components/Footer";
import Testimonials from "../components/Testimonials";
import {
  Car,
  Building2,
  Calendar,
  HandCoins,
  MapPin,
  ShieldCheck,
  Users,
} from "lucide-react";
import { NavLink } from "react-router-dom";

const choosePulo = [
  {
    title: "Pulo Assistant",
    description: "Local experts who bring destinations to life.",
    icon: Users,
    to: "/travel-assistant",
  },
  {
    title: "Rides",
    description: "Safe, reliable, and flexible transport options.",
    icon: Car,
    to: "/rides",
  },
  {
    title: "Stay",
    description: "Verified accommodations for every traveler.",
    icon: Building2,
    to: "/stays",
  },
];

const perks = [
  {
    icon: MapPin,
    title: "Local Knowledge:",
    text: "Handpicked guides who know every hidden spot.",
  },
  {
    icon: ShieldCheck,
    title: "Your Safety, Our Priority:",
    text: "Verified drivers, guides, and hosts.",
  },
  {
    icon: HandCoins,
    title: "No Surprises:",
    text: "Upfront pricing, no hidden fees.",
  },
  {
    icon: Calendar,
    title: "Flexible Stays:",
    text: "Short-term or long-term rentals for any trip.",
  },
];

const team = Array.from({ length: 8 }, (_, index) => ({
  id: index,
  name: index % 2 === 0 ? "Olabode Martins" : "Andrew Fred",
  role: "CEO & Co-Founder",
  image: "/images/member.jpg",
}));

function About() {
  return (
    <div className="bg-white min-h-screen">
      <Header />

      <main className="sm:space-y-16 ">
        {/* Hero */}
        <section className="relative w-full  flex items-center justify-center">
          <img
            src="/images/about-hero.jpg"
            alt="City skyline"
            className="w-full h-[300px] sm:h-[420px] xl:h-[85vh] object-cover"
          />
          <div className="absolute inset-0 flex flex-col  items-center justify-center text-center text-white px-4">
            <h1 className="text-xl md:text-4xl font-semibold leading-tight max-w-3xl">
              Redefining Travel - One Ride, One Guide, One Stay at a Time
            </h1>
            <NavLink
              to="/explore/lagos"
              className="mt-6 inline-flex bg-[#570033] hover:bg-[#6c0a46] px-12 py-3 rounded-lg text-sm font-semibold shadow no-underline text-white"
            >
              Discover More
            </NavLink>
          </div>
        </section>

        {/* Who we are */}
        <section className="py-12">
          <div className="text-center max-w-3xl mx-auto px-4">
            <h2 className="text-2xl font-semibold text-[#45012A] mb-3">
              Who We Are
            </h2>
            <p className="text- text-gray-700 font-medium leading-relaxed">
              Our crew is constantly growing, but we all work toward one goal:
              to make travel simple, seamless, and stress-free for users
              worldwide.
            </p>
          </div>
        </section>

        {/* Mission / Vision */}
        <section className="sm:bg-gray-100 sm:py-12">
          <div className="max-w-[80%] mx-auto px-4 grid md:grid-cols-2 sm:gap-10 items-center">
            <div className="sm:space-y-3">
              <h3 className="text-2xl max-sm:text-center font-semibold text-[#570033]">
                Our Mission
              </h3>
              <p className="text-sm text-gray-700 font-medium leading-relaxed">
                We believe travel should be simple, safe, and enriching. Whether
                you need a trusted ride, a knowledgeable local guide, or a
                verified place to stay, Pulo ensures a seamless and stress-free
                experience.
              </p>
            </div>

            <div className="max-sm:hidden flex justify-end">
              <img
                src="/images/howitworks2.jpg"
                alt="Mission"
                className="w-[80%] h-60 object-cover object-top rounded-lg"
              />
            </div>
          </div>

          <div className="max-w-[82%] mx-auto px-4 grid md:grid-cols-2 gap-10 items-center sm:mt-10">
            <div className="max-sm:hidden order-2 md:order-1">
              <img
                src="/images/howitworks3.jpg"
                alt="Vision"
                className="w-[80%] h-60 object-cover object-top rounded-lg"
              />
            </div>
            <div className="space-y-3 order-1 md:order-2">
              <h3 className="text-2xl max-sm:text-center font-semibold text-[#570033]">
                Our Vision
              </h3>
              <p className="text-sm text-gray-700 font-medium leading-relaxed">
                We aim to lead the future of travel in Nigeria and beyond by
                connecting global travelers to authentic local expertise, safe
                transport, and trusted stays.
              </p>
            </div>
          </div>
        </section>

        {/* Why pulo*/}
        <section className="max-w-[80%] mx-auto space-y-8 max-sm:mt-6">
          <div className="text-center  space-y-4 max-w-2xl mx-auto">
            <h2 className="text-2xl font-semibold text-[#570033]">
              Why Choose Pulo?
            </h2>
            <p className="text-[#2B333C] text- font-medium">
              Our crew is constantly growing, but we all work toward one goal:
              to make travel simple, seamless, and stress-free for users
              worldwide.
            </p>
          </div>

          <div className="grid gap-4 sm:gap-6 grid-cols-2 md:grid-cols-3">
            {choosePulo.map(({ title, description, icon: Icon, to }, index) => (
              <NavLink
                key={title}
                to={to}
                className={`rounded-[20px] sm:rounded-[24px] h-full flex flex-col items-center justify-center text-white py-6 px-4 sm:py-8 gap-3 no-underline bg-[#61013B] hover:bg-[#7a0051] transition-colors ${
                  index === 0 ? "col-span-2 md:col-span-1" : ""
                }`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg sm:text-xl font-bold">{title}</h3>
                  </div>

                  <p className="text-sm sm:text-lg text-white text-center font-medium leading-relaxed">
                    {description}
                  </p>
                </div>
              </NavLink>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2 ">
            {perks.map(({ title, text, icon: Icon }) => (
              <div
                key={title}
                className="flex gap-4 rounded-2xl p-4 max-sm:border max-sm:border-gray-200 max-sm:shadow-md "
              >
                <div className="h-12 w-12 rounded-full max-sm:bg-[#EFE6EB]  text-[#7a0051] flex items-center justify-center">
                  <Icon className="h-6 w-6 " />
                </div>
                <div className="sm:space-y-3">
                  <p className="font-bold sm:text-lg text-[#570033]">{title}</p>
                  <p className="text-sm text-[2B333C] font-medium">{text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 md:mt-12">
            <NavLink
              to="/explore/lagos"
              className="inline-flex w-full sm:w-auto px-12 py-3 rounded-md bg-[#570033] text-[#EFE6EB] font-medium no-underline hover:bg-[#3f0125] justify-center"
            >
              Explore Now
            </NavLink>
          </div>
        </section>

        {/* Team */}
        <section className="max-sm:mt-6 space-y-12">
          <div className="text-center px-4 space-y-6">
            <h2 className="text-3xl font-semibold text-[#61013B]">
              Meet Our Team
            </h2>
            <p className="text- text-gray-700 max-w-2xl font-medium mx-auto">
              Our crew is constantly growing, but we all work toward one goal:
              to make travel simple, seamless, and stress-free for users
              worldwide.
            </p>
          </div>

          <div className="max-w-[80%] mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6">
            {team.map((member) => (
              <div key={member.id} className="flex flex-col items-center">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full aspect-[4/5] object-cover rounded-2xl"
                />

                <div className="mt-4 space-y-1 text-center">
                  <p className="text-base font-semibold text-gray-900">
                    {member.name}
                  </p>
                  <p className="text-sm text-gray-600 font-medium">
                    {member.role}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Earn CTA */}
        <section className=" max-w-5xl mx-auto text-center max-sm:mt-8">
          <h2 className="text-2xl font-semibold text-[#570033]">
            Earn with Pulo
          </h2>
          <p className="text-[#2B333C] mt-4 font-medium leading-relaxed">
            Love meeting new people and sharing amazing stories? Join us as a
            Pulo Assistant and turn your passion for travel into an
            adventure-filled career. Connect with travelers from all over the
            world.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
            <NavLink
              to="/auth/welcome"
              className="px-20 py-3 rounded-lg border border-[#570033] bg-[#570033] text-white font-medium no-underline"
            >
              Join Pulo
            </NavLink>
            <NavLink
              to="/auth/login"
              className="px-20 py-3 rounded-lg border border-[#570033]  text-[#570033] font-medium no-underline"
            >
              Log in
            </NavLink>
          </div>
        </section>

        <Testimonials className="mt-6" />
      </main>

      <Footer />
    </div>
  );
}

export default About;
